import { Environment, Modes } from "@gemma/configuration";
import { EventBus, IAggregate, ITypedEvent } from "@gemma/events";
/* global CONFIG: false */
import { Loggers, Sinks } from "@gemma/logging";
import { getCookieDomain, getTenantInfo } from "@gemma/web-common";

import { EventBus as EmitterEventBus } from "@gemma/eventemitter";
import { IWebApplicationConstants } from "@timesheets/webapp-support";
import { Protocols } from "@gemma/typescript-types";
import URI from "urijs";
import { getOrigin } from "@gemma/utils";

const config: {
  instance: Modes;
  api: {
    protocol: Protocols;
    hostname: string;
    port: number;
  };
  domainPrefix: string;
  my: {
    protocol: Protocols;
    hostname: string;
    port: number;
  };
  releaseDate: string;
  showErrorOverlay: boolean;
  showInternalErrors: boolean;
  subZone: string;
  version: { build: string };
} =
  //@ts-ignore
  CONFIG;

const id = "Timesheets";
const loggers = new Loggers(`${config.instance}`, prefix => new Sinks.ConsoleSink(prefix, console));
const defaultLogger = loggers.getLogger(id);
const domainPrefix = config.domainPrefix;
const eventBus = new EventBus<ITypedEvent<string, unknown, string>, unknown, string, IAggregate<string>>(loggers, defaultLogger);
const listenerEventBuses = {
  system: new EmitterEventBus<boolean>("."),
  application: new EmitterEventBus<boolean>("."),
};
const tenantInfo = getTenantInfo(window.location.hostname, { domainPrefix: config.domainPrefix, subZone: config.subZone });
const myRootUri = new URI(getOrigin(window));
myRootUri.subdomain(tenantInfo.environmentSpecificTenantName);
myRootUri.port(config.my.port.toString());
const subZone = config.subZone;
const unbrandedApiRootUri = new URI(`${config.api.protocol}://${config.api.hostname}:${config.api.port}`);
const unbrandedMyRootUri = new URI(`${config.my.protocol}://${config.my.hostname}:${config.my.port}`);
const cookieDomain = getCookieDomain(config.instance);
const numberFormatter = new Intl.NumberFormat("en-GB");

export const WebApplicationConstants: IWebApplicationConstants = {
  api: {
    hostname: config.api.hostname,
    port: config.api.port,
    protocol: config.api.protocol,
  },
  authCookie: {
    value: `auth2=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${cookieDomain}; samesite=lax; secure`,
    sig: `auth2.sig=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${cookieDomain}; samesite=lax; secure`,
  },
  cookieDomain,
  currentTenant: getTenantInfo(window.location.hostname, { domainPrefix, subZone }).canonicalTenantName,
  defaultLogger,
  domainPrefix,
  eventBus: eventBus as any,
  environment: process.env.NODE_ENV as Environment,
  id,
  instance: config.instance,
  // @ts-ignore
  isIE11: !!window.MSInputMethodContext && !!document.documentMode,
  listenerEventBuses,
  loggers,
  my: {
    unbrandedRootUrl: unbrandedMyRootUri.toString(),
    hostname: myRootUri.hostname(),
    port: config.my.port,
    protocol: config.my.protocol,
    rootUri: myRootUri,
  },
  notifyOptions: { duration: 5000 },
  numberFormatter,
  responsiveBreakpoints: {
    xs: {
      min: 0,
      max: 575,
    },
    sm: {
      min: 576,
      max: 767,
    },
    md: {
      min: 768,
      max: 991,
    },
    lg: {
      min: 992,
      max: 1199,
    },
    xl: {
      min: 1200,
      max: Infinity,
    },
  },
  releaseDate: config.releaseDate,
  show: {
    errorOverlay: config.showErrorOverlay,
    internalErrors: config.showInternalErrors,
    ids: false,
  },
  subZone,
  systemInformation: `SaveTrees Online Timesheet software- version: ${config.version.build}, release date: ${config.releaseDate}`,
  version: config.version,
  unbrandedApiRootUri,
  unbrandedMyRootUri,
};
